"use client"

import * as React from "react"
import { PropsWithChildren } from "react"
import {
    useReactTable,
    ColumnDef,
    flexRender,
    getCoreRowModel,
} from "@tanstack/react-table"
import {
    Table,
    TableHeader,
    TableHead,
} from "../../../shared/components/ui/table"
import { Checkbox } from "../../../shared/components/ui/checkbox"
import { cn, formatPhoneNumber } from "../../../shared/lib/utils"
import { buildRegionAwareHref } from "../../../shared/lib/utils"
import { buildHomeownersDetailUrlWithReturn } from "../../../shared/lib/utils/navigation-helpers"
import Link from "next/link"
import {
    HOMEOWNERS_ROUTES,
    DASHBOARD_ROUTE,
} from "../../../shared/constants/pages-routes"
import { HomeOwner } from "../types/backend-response.type"
import HomeOwnersTableBody from "./home-owners-table-body"
import TableRow from "./table-row"
import { MapPin, Workflow, Filter } from "lucide-react"
import { format } from "date-fns"
import { Calendar } from "../../../shared/components/ui/calendar"
import { Button } from "@/shared/components/ui/button"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../../../shared/components/ui/popover"
import { overallStatusSelectableOptions, goodwillStatusOptions } from "../constants/options"
import {
    displayOverallStatusLabel,
    normalizeOverallStatus,
    normalizeOverallStatusList,
    compactStatusLabelForHomeownersList,
} from "../lib/overall-status.utils"
import { Input } from "../../../shared/components/ui/input"
import DeleteHomeownerDialog from "./delete-homeowner-dialog"
import { useRegion } from "@/shared/hooks/use-region"
import { getStatusButtonClass, getGoodwillStatusButtonClass } from "../lib/status-button-colors"
import {
  shouldMaskHomeownerData,
  maskHomeownerLastName,
  maskHomeownerPhone,
  maskHomeownerAddress,
} from "@/shared/lib/utils/homeowner-privacy.utils"

function HomeOwnersTable({
    data,
    isLoading,
    hideOwnerDetails = false,
    hideFlowchartLinks = false,
    mapRoute = DASHBOARD_ROUTE.map,
    userRole = null,
    sessionSub = null,
    overallStatusFilter = "",
    onOverallStatusFilter,
    reliefStatusFilter = "",
    onReliefStatusFilter,
    rebuildingStatusFilter = "",
    onRebuildingStatusFilter,
    goodwillStatusFilter = "",
    onGoodwillStatusFilter,
    homeownerNameFilter = "",
    onHomeownerNameFilter,
    homeownerNames = [],
    cityFilter = "",
    streetFilter = "",
    onAddressFilter,
    updatedFromFilter = "",
    updatedToFilter = "",
    onUpdatedFilter,
}: {
    data: HomeOwner[]
    isLoading: boolean
    hideOwnerDetails?: boolean
    hideFlowchartLinks?: boolean
    mapRoute?: string
    userRole?: string | null
    sessionSub?: string | number | null
    overallStatusFilter?: string
    onOverallStatusFilter?: (statuses: string[] | null) => void
    reliefStatusFilter?: string
    onReliefStatusFilter?: (statuses: string[] | null) => void
    rebuildingStatusFilter?: string
    onRebuildingStatusFilter?: (statuses: string[] | null) => void
    goodwillStatusFilter?: string
    onGoodwillStatusFilter?: (statuses: string[] | null) => void
    homeownerNameFilter?: string
    onHomeownerNameFilter?: (value: string | null) => void
    homeownerNames?: { name: string; userId?: number }[]
    cityFilter?: string
    streetFilter?: string
    onAddressFilter?: (opts: { city: string | null; street: string | null }) => void
    updatedFromFilter?: string
    updatedToFilter?: string
    onUpdatedFilter?: (from: string | null, to: string | null) => void
}) {
    // Home Details link is available to all users
    const canViewOwnerDetails = true
    const { regionSlug } = useRegion()
    const effectiveMapRoute = React.useMemo(() => buildRegionAwareHref(regionSlug, mapRoute), [regionSlug, mapRoute])

    const table = useReactTable({
        data,
        columns: getColumns(hideOwnerDetails, hideFlowchartLinks, effectiveMapRoute, regionSlug, canViewOwnerDetails, overallStatusFilter, onOverallStatusFilter, reliefStatusFilter, onReliefStatusFilter, rebuildingStatusFilter, onRebuildingStatusFilter, goodwillStatusFilter, onGoodwillStatusFilter, homeownerNameFilter, onHomeownerNameFilter, homeownerNames, cityFilter, streetFilter, onAddressFilter, updatedFromFilter, updatedToFilter, onUpdatedFilter, userRole, sessionSub),
        getCoreRowModel: getCoreRowModel(),
    })
    return (
        <div className="w-full">
            <div className="rounded-md">
                <Table>
                    <TableHeader className="[&_tr]:border-b-0 bg-muted">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead
                                        key={header.id}
                                        className="w-max"
                                        data-print-hide-col={
                                            header.column.id === 'select' ||
                                            header.column.id === 'actions'
                                                ? 'true'
                                                : undefined
                                        }
                                    >
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                        )}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <HomeOwnersTableBody table={table} isLoading={isLoading} />
                </Table>
            </div>
        </div>
    )
}

// Autocomplete combobox for Homeowner Name: input + name suggestions
const SUGGESTIONS_MAX = 80

function HomeownerNameFilterContent({
    value,
    onApply,
    nameSuggestions = [],
    userRole = null,
    sessionSub = null,
}: {
    value: string
    onApply: (v: string | null) => void
    nameSuggestions?: { name: string; userId?: number }[]
    userRole?: string | null
    sessionSub?: string | number | null
}) {
    const [local, setLocal] = React.useState(value)
    const [isListOpen, setIsListOpen] = React.useState(false)

    React.useEffect(() => {
        setLocal(value)
    }, [value])

    const suggestions = React.useMemo(() => {
        if (!nameSuggestions.length) return []
        const q = local.trim().toLowerCase()
        const filtered = q
            ? nameSuggestions.filter((s) => s.name.toLowerCase().includes(q))
            : nameSuggestions
        return filtered.slice(0, SUGGESTIONS_MAX)
    }, [nameSuggestions, local])

    const showList = isListOpen && suggestions.length > 0

    const handleSelect = (name: string) => {
        setLocal(name)
        onApply(name)
        setIsListOpen(false)
    }

    const hasActive = !!value

    return (
        <div className="w-64 p-2 space-y-2">
            <Input
                value={local}
                onChange={(e) => {
                    setLocal((e.target as HTMLInputElement).value)
                    setIsListOpen(true)
                }}
                onFocus={() => setIsListOpen(true)}
                onBlur={() => setTimeout(() => setIsListOpen(false), 150)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onApply(local.trim() || null); } }}
                placeholder="Type to search names…"
                className="h-8"
            />
            {showList && (
                <div className="border rounded-md bg-muted/50 max-h-40 overflow-y-auto">
                    {suggestions.map((s) => {
                        const mask = shouldMaskHomeownerData(userRole, sessionSub, s.userId)
                        const display = mask ? maskHomeownerLastName(s.name) : s.name
                        return (
                            <button
                                key={`${s.name}-${s.userId ?? ''}`}
                                type="button"
                                className="w-full px-2 py-1.5 text-left text-sm hover:bg-accent focus:bg-accent focus:outline-none"
                                onMouseDown={(e) => {
                                    e.preventDefault()
                                    handleSelect(s.name)
                                }}
                            >
                                {display}
                            </button>
                        )
                    })}
                </div>
            )}
            <div className="flex gap-2">
                <Button size="sm" variant="default" onClick={() => onApply(local.trim() || null)}>
                    Apply
                </Button>
                {hasActive && (
                    <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => onApply(null)}>
                        Clear
                    </Button>
                )}
            </div>
        </div>
    )
}

// Filter popover content for Address (City + Street)
function AddressFilterContent({
    cityFilter,
    streetFilter,
    onAddressFilter,
}: {
    cityFilter: string
    streetFilter: string
    onAddressFilter: (opts: { city: string | null; street: string | null }) => void
}) {
    const [city, setCity] = React.useState(cityFilter)
    const [street, setStreet] = React.useState(streetFilter)
    React.useEffect(() => {
        setCity(cityFilter)
        setStreet(streetFilter)
    }, [cityFilter, streetFilter])
    const hasActive = !!cityFilter || !!streetFilter
    const apply = () => onAddressFilter({ city: city.trim() || null, street: street.trim() || null })
    return (
        <div className="w-56 p-2 space-y-2">
            <div>
                <label className="text-xs text-muted-foreground">City</label>
                <Input
                    value={city}
                    onChange={(e) => setCity((e.target as HTMLInputElement).value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }}
                    placeholder="Filter by city…"
                    className="h-8 mt-0.5"
                />
            </div>
            <div>
                <label className="text-xs text-muted-foreground">Street</label>
                <Input
                    value={street}
                    onChange={(e) => setStreet((e.target as HTMLInputElement).value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }}
                    placeholder="Filter by street…"
                    className="h-8 mt-0.5"
                />
            </div>
            <div className="flex gap-2">
                <Button
                    size="sm"
                    variant="default"
                    onClick={apply}
                >
                    Apply
                </Button>
                {hasActive && (
                    <Button
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground"
                        onClick={() => onAddressFilter({ city: null, street: null })}
                    >
                        Clear
                    </Button>
                )}
            </div>
        </div>
    )
}

// Multi-select filter for Overall Status (like Work Orders Work Scope: local state + Apply/Clear)
function OverallStatusFilterContent({
    value,
    onApply,
    options,
}: {
    value: string[]
    onApply: (v: string[] | null) => void
    options: readonly { value: string; label: string }[]
}) {
    const [local, setLocal] = React.useState<string[]>(Array.isArray(value) ? value.slice() : [])
    React.useEffect(() => {
        setLocal(Array.isArray(value) ? value.slice() : [])
    }, [value])
    const hasActive = local.length > 0
    const toggle = (v: string) => {
        setLocal((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]))
    }
    return (
        <div
            className="w-56 p-2 space-y-2"
            onKeyDown={(e) => {
                if (e.key !== "Enter") return
                if ((e.target as HTMLElement)?.closest?.('[data-filter-action="apply"], [data-filter-action="clear"]')) return
                e.preventDefault()
                onApply(local.length ? local : null)
            }}
        >
            <div className="border rounded-md bg-muted/50 max-h-48 overflow-y-auto">
                {options.map((o) => (
                    <label key={o.value} className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-muted/60">
                        <Checkbox checked={local.includes(o.value)} onCheckedChange={() => toggle(o.value)} />
                        <span className="text-sm">{o.label}</span>
                    </label>
                ))}
            </div>
            <div className="flex gap-2">
                <Button size="sm" variant="default" data-filter-action="apply" onClick={() => onApply(local.length ? local : null)}>
                    Apply
                </Button>
                {hasActive && (
                    <Button size="sm" variant="ghost" className="text-muted-foreground" data-filter-action="clear" onClick={() => onApply(null)}>
                        Clear
                    </Button>
                )}
            </div>
        </div>
    )
}

// Date range filter for Updated column (YYYY-MM-DD strings)
function UpdatedFilterContent({
    updatedFrom,
    updatedTo,
    onApply,
}: {
    updatedFrom: string
    updatedTo: string
    onApply: (from: string | null, to: string | null) => void
}) {
    const parse = (s: string) => {
        if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return undefined
        const [y, m, d] = s.split("-").map(Number)
        return new Date(y, m - 1, d)
    }
    const fromDate = parse(updatedFrom)
    const toDate = parse(updatedTo)
    const [range, setRange] = React.useState<{ from?: Date; to?: Date } | undefined>(() =>
        fromDate || toDate ? { from: fromDate, to: toDate } : undefined
    )
    React.useEffect(() => {
        setRange(fromDate || toDate ? { from: fromDate, to: toDate } : undefined)
    }, [updatedFrom, updatedTo, fromDate, toDate])
    const hasActive = !!updatedFrom || !!updatedTo
    const apply = () => {
        const fromStr = range?.from ? format(range.from, "yyyy-MM-dd") : null
        // Single-date pick: treat as "on this date only" by using the same day for to
        const toStr = range?.to ? format(range.to, "yyyy-MM-dd") : (range?.from ? format(range.from, "yyyy-MM-dd") : null)
        onApply(fromStr, toStr)
    }
    return (
        <div className="w-auto p-2 space-y-2" onKeyDown={(e) => { if (e.key !== "Enter") return; if ((e.target as HTMLElement)?.closest?.("[data-filter-action]")) return; e.preventDefault(); apply(); }}>
            <Calendar
                mode="range"
                selected={range === undefined ? undefined : { from: range.from, to: range.to }}
                onSelect={setRange}
                defaultMonth={range?.from || range?.to || new Date()}
                numberOfMonths={2}
                initialFocus
            />
            <div className="flex gap-2">
                <Button size="sm" variant="default" data-filter-action="apply" onClick={apply}>Apply</Button>
                {hasActive && <Button size="sm" variant="ghost" className="text-muted-foreground" data-filter-action="clear" onClick={() => onApply(null, null)}>Clear</Button>}
            </div>
        </div>
    )
}

const getColumns = (
    hideOwnerDetails: boolean, 
    hideFlowchartLinks: boolean, 
    mapRoute: string,
    regionSlug: string | null,
    canViewOwnerDetails: boolean = false,
    overallStatusFilter: string = "",
    onOverallStatusFilter?: (statuses: string[] | null) => void,
    reliefStatusFilter: string = "",
    onReliefStatusFilter?: (statuses: string[] | null) => void,
    rebuildingStatusFilter: string = "",
    onRebuildingStatusFilter?: (statuses: string[] | null) => void,
    goodwillStatusFilter: string = "",
    onGoodwillStatusFilter?: (statuses: string[] | null) => void,
    homeownerNameFilter: string = "",
    onHomeownerNameFilter?: (value: string | null) => void,
    homeownerNames: { name: string; userId?: number }[] = [],
    cityFilter: string = "",
    streetFilter: string = "",
    onAddressFilter?: (opts: { city: string | null; street: string | null }) => void,
    updatedFromFilter: string = "",
    updatedToFilter: string = "",
    onUpdatedFilter?: (from: string | null, to: string | null) => void,
    userRole: string | null = null,
    sessionSub: string | number | null = null
): ColumnDef<HomeOwner>[] => [
    {
        id: "select",
        header: ({ table }) => (
            <Checkbox
                checked={
                    table.getIsAllPageRowsSelected() ||
                    (table.getIsSomePageRowsSelected() && "indeterminate")
                }
                onCheckedChange={(value) =>
                    table.toggleAllPageRowsSelected(!!value)
                }
                aria-label="Select all"
                className="rounded-md border-muted-foreground/70 data-[state=checked]:border-primary"
            />
        ),
        cell: ({ row }) => (
            <Checkbox
                checked={row.getIsSelected()}
                onCheckedChange={(value) => row.toggleSelected(!!value)}
                aria-label="Select row"
                className="rounded-md border-muted-foreground/70 data-[state=checked]:border-primary"
            />
        ),
        enableSorting: false,
        enableHiding: false,
    },
    {
        accessorKey: "user.fullname",
        header: () => {
            if (!onHomeownerNameFilter) {
                return <InnerTh>Homeowner Name</InnerTh>
            }
            const hasActive = !!homeownerNameFilter
            return (
                <InnerTh>
                    <div className="flex items-center gap-1.5">
                        <span>Homeowner Name</span>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 rounded"
                                    aria-label="Filter by Homeowner Name"
                                >
                                    <Filter className={cn("h-3.5 w-3.5", hasActive && "text-primary")} />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-auto p-0">
                                <HomeownerNameFilterContent
                                    value={homeownerNameFilter}
                                    onApply={onHomeownerNameFilter}
                                    nameSuggestions={homeownerNames}
                                    userRole={userRole}
                                    sessionSub={sessionSub}
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                </InnerTh>
            )
        },
        cell: ({ row }) => {
            const name = row.original.user.fullname
            const mask = shouldMaskHomeownerData(userRole, sessionSub, row.original.user?.id)
            const displayName = mask ? maskHomeownerLastName(name) : name
            return (
                <Link
                    href={buildRegionAwareHref(regionSlug, buildHomeownersDetailUrlWithReturn(row.original.id))}
                    className="text-primary hover:underline"
                >
                    {displayName}
                </Link>
            )
        },
    },
    {
        accessorKey: "home.address",
        header: () => {
            if (!onAddressFilter) {
                return <InnerTh>Address</InnerTh>
            }
            const hasActive = !!cityFilter || !!streetFilter
            return (
                <InnerTh>
                    <div className="flex items-center gap-1.5">
                        <span>Address</span>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 rounded"
                                    aria-label="Filter by City or Street"
                                >
                                    <Filter className={cn("h-3.5 w-3.5", hasActive && "text-primary")} />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-auto p-0">
                                <AddressFilterContent
                                    cityFilter={cityFilter}
                                    streetFilter={streetFilter}
                                    onAddressFilter={onAddressFilter}
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                </InnerTh>
            )
        },
        cell: ({ row }) => {
            const raw = row.original.home?.address || "No address"
            const mask = shouldMaskHomeownerData(userRole, sessionSub, row.original.user?.id)
            const address = mask ? (raw === "No address" ? raw : maskHomeownerAddress(raw)) : raw
            const mapLink = buildMapLink(mapRoute, row.original.home?.id)
            return (
                <Link href={mapLink} className="text-primary hover:underline">
                    {address}
                </Link>
            )
        },
    },
    {
        accessorKey: "user.phone",
        header: () => <InnerTh>Phone Number</InnerTh>,
        cell: ({ row }) => {
            const mask = shouldMaskHomeownerData(userRole, sessionSub, row.original.user?.id)
            return mask
                ? maskHomeownerPhone(row.original.user.phone)
                : formatPhoneNumber(row.original.user.phone)
        },
    },
    {
        accessorKey: "home.homeStatusInfo.overallStatus",
        header: () => {
            const selected = normalizeOverallStatusList(
                (overallStatusFilter || "").split(",").map((s) => s.trim()).filter(Boolean)
            )
            if (!onOverallStatusFilter) {
                return <InnerTh>Overall Status</InnerTh>
            }
            return (
                <InnerTh>
                    <div className="flex items-center gap-1.5">
                        <span>Overall Status</span>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 rounded"
                                    aria-label="Filter by Overall Status"
                                >
                                    <Filter className={cn("h-3.5 w-3.5", selected.length > 0 && "text-primary")} />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-auto p-0">
                                <OverallStatusFilterContent
                                    value={selected}
                                    onApply={onOverallStatusFilter}
                                    options={overallStatusSelectableOptions}
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                </InnerTh>
            )
        },
        cell: ({ row }) => {
            const overallStatus =
                displayOverallStatusLabel(row.original.home?.homeStatusInfo?.overallStatus) || "—"
            const displayStatus = compactStatusLabelForHomeownersList(overallStatus)
            const href = buildRegionAwareHref(regionSlug, buildHomeownersDetailUrlWithReturn(row.original.id))
            const statusClass = getStatusButtonClass(row.original.home?.homeStatusInfo?.overallStatus)
            return (
                <Link href={href}>
                    <Button
                        variant="default"
                        size="sm"
                        className={`${statusClass} font-bold whitespace-nowrap`}
                    >
                        {displayStatus}
                    </Button>
                </Link>
            )
        },
    },
    {
        accessorKey: "home.reliefStatus",
        header: () => {
            const selected = (reliefStatusFilter || "").split(",").map((s) => s.trim()).filter(Boolean)
            if (!onReliefStatusFilter) {
                return <InnerTh>Relief Status</InnerTh>
            }
            return (
                <InnerTh>
                    <div className="flex items-center gap-1.5">
                        <span>Relief Status</span>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 rounded"
                                    aria-label="Filter by Relief Status"
                                >
                                    <Filter className={cn("h-3.5 w-3.5", selected.length > 0 && "text-primary")} />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-auto p-0">
                                <OverallStatusFilterContent
                                    value={selected}
                                    onApply={onReliefStatusFilter}
                                    options={goodwillStatusOptions}
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                </InnerTh>
            )
        },
        cell: ({ row }) => {
            const label = row.original.home?.reliefStatus || "—"
            const displayLabel = compactStatusLabelForHomeownersList(label)
            const href = buildRegionAwareHref(regionSlug, buildHomeownersDetailUrlWithReturn(row.original.id))
            const statusClass = getGoodwillStatusButtonClass(row.original.home?.reliefStatus)
            return (
                <Link href={href}>
                    <Button
                        variant="default"
                        size="sm"
                        className={`${statusClass} font-bold whitespace-nowrap`}
                    >
                        {displayLabel}
                    </Button>
                </Link>
            )
        },
    },
    {
        accessorKey: "home.rebuildingStatus",
        header: () => {
            const selected = (rebuildingStatusFilter || "").split(",").map((s) => s.trim()).filter(Boolean)
            if (!onRebuildingStatusFilter) {
                return <InnerTh>Rebuilding Status</InnerTh>
            }
            return (
                <InnerTh>
                    <div className="flex items-center gap-1.5">
                        <span>Rebuilding Status</span>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 rounded"
                                    aria-label="Filter by Rebuilding Status"
                                >
                                    <Filter className={cn("h-3.5 w-3.5", selected.length > 0 && "text-primary")} />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-auto p-0">
                                <OverallStatusFilterContent
                                    value={selected}
                                    onApply={onRebuildingStatusFilter}
                                    options={goodwillStatusOptions}
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                </InnerTh>
            )
        },
        cell: ({ row }) => {
            const label = row.original.home?.rebuildingStatus || "—"
            const displayLabel = compactStatusLabelForHomeownersList(label)
            const href = buildRegionAwareHref(regionSlug, buildHomeownersDetailUrlWithReturn(row.original.id))
            const statusClass = getGoodwillStatusButtonClass(row.original.home?.rebuildingStatus)
            return (
                <Link href={href}>
                    <Button
                        variant="default"
                        size="sm"
                        className={`${statusClass} font-bold whitespace-nowrap`}
                    >
                        {displayLabel}
                    </Button>
                </Link>
            )
        },
    },
    {
        accessorKey: "home.goodwillStatus",
        header: () => {
            const selected = (goodwillStatusFilter || "").split(",").map((s) => s.trim()).filter(Boolean)
            if (!onGoodwillStatusFilter) {
                return <InnerTh>Goodwill Status</InnerTh>
            }
            return (
                <InnerTh>
                    <div className="flex items-center gap-1.5">
                        <span>Goodwill Status</span>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 rounded"
                                    aria-label="Filter by Goodwill Status"
                                >
                                    <Filter className={cn("h-3.5 w-3.5", selected.length > 0 && "text-primary")} />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-auto p-0">
                                <OverallStatusFilterContent
                                    value={selected}
                                    onApply={onGoodwillStatusFilter}
                                    options={goodwillStatusOptions}
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                </InnerTh>
            )
        },
        cell: ({ row }) => {
            const goodwillStatus = row.original.home?.goodwillStatus || "—"
            const displayStatus = compactStatusLabelForHomeownersList(goodwillStatus)
            const href = buildRegionAwareHref(regionSlug, buildHomeownersDetailUrlWithReturn(row.original.id))
            const statusClass = getGoodwillStatusButtonClass(row.original.home?.goodwillStatus)
            return (
                <Link href={href}>
                    <Button
                        variant="default"
                        size="sm"
                        className={`${statusClass} font-bold whitespace-nowrap`}
                    >
                        {displayStatus}
                    </Button>
                </Link>
            )
        },
    },
    {
        accessorKey: "home.updatedAt",
        header: () => {
            if (!onUpdatedFilter) return <InnerTh>Updated</InnerTh>
            const hasActive = !!updatedFromFilter || !!updatedToFilter
            return (
                <InnerTh>
                    <div className="flex items-center gap-1.5">
                        <span>Updated</span>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-7 w-7 rounded" aria-label="Filter by Updated date">
                                    <Filter className={cn("h-3.5 w-3.5", hasActive && "text-primary")} />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-auto p-0">
                                <UpdatedFilterContent
                                    updatedFrom={updatedFromFilter}
                                    updatedTo={updatedToFilter}
                                    onApply={onUpdatedFilter}
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                </InnerTh>
            )
        },
        cell: ({ row }) => {
            const raw = row.original.home?.updatedAt
            return (
                <span className="text-muted-foreground text-sm whitespace-nowrap">
                    {raw ? new Date(raw).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}
                </span>
            )
        },
    },
    {
        id: "actions",
        header: () => <InnerTh />,
        cell: ({ row }) => {
            return (
                <div className="flex items-center justify-start gap-1">
                    <div className="flex items-center gap-2 lg:gap-3 shrink-0">
                        {!hideOwnerDetails && (
                            <Link
                                href={buildRegionAwareHref(regionSlug, buildHomeownersDetailUrlWithReturn(row.original.id))}
                                className="lg:hidden"
                            >
                                <Button 
                                    variant="default" 
                                    size="sm"
                                    className="bg-primary hover:bg-primary/90 text-white font-bold whitespace-nowrap"
                                >
                                    Home Details
                                </Button>
                            </Link>
                        )}
                    </div>
                    <div className="flex gap-1.5 lg:gap-2 items-center shrink-0">
                        <Button
                            variant={"default"}
                            size={"icon"}
                            asChild
                            className="rounded-md bg-foreground hover:bg-foreground/90 text-white w-7 h-7 lg:w-8 lg:h-8"
                        >
                            <Link href={buildMapLink(mapRoute, row.original.home?.id)}>
                                <MapPin className="w-4 h-4" />
                            </Link>
                        </Button>
                        {!hideFlowchartLinks && (
                            <Button
                                variant={"secondary"}
                                size={"icon"}
                                className="rounded-md bg-foreground hover:bg-foreground/90 text-white w-7 h-7 lg:w-8 lg:h-8 hidden lg:flex"
                                asChild
                            >
                                <Link
                                    href={buildRegionAwareHref(regionSlug, `${buildHomeownersDetailUrlWithReturn(row.original.id)}&tab=workflow`)}
                                >
                                    <Workflow className="w-4 h-4" />
                                </Link>
                            </Button>
                        )}
                        <DeleteHomeownerDialog homeowner={row.original} />
                    </div>
                </div>
            )
        },
    },
]

// Helper function to build map link with homeId parameter
function buildMapLink(route: string, homeId?: number | null): string {
    const [base, queryString] = route.split('?')
    const params = new URLSearchParams(queryString || '')
    if (homeId) {
        params.set('homeId', homeId.toString())
    } else {
        params.delete('homeId')
    }
    const query = params.toString()
    return query ? `${base}?${query}` : base
}

const InnerTh = (props: PropsWithChildren & { className?: string }) => {
    return (
        <div className={cn("text-left w-max", props.className)}>
            {props.children}
        </div>
    )
}

export default HomeOwnersTable
